/**
 * The document revisions of a beta-lane contract (ADR-0099): every issued,
 * signed or superseded compile, newest first. Metadata only — the frozen
 * IR itself is served by `contracts.ir_preview` / the PDF route.
 */

import { useTenantQuery } from '../../query.js';
import { tenantApi, type IrRevision } from '../../api.js';

const STATUS_LABEL: Record<IrRevision['status'], string> = {
  draft: 'Utkast',
  issued: 'Utfärdad',
  signed: 'Signerad',
  superseded: 'Ersatt',
};

const STATUS_CLS: Record<IrRevision['status'], string> = {
  draft: 'border-stone-300 bg-stone-100 text-stone-700',
  issued: 'border-indigo-300 bg-indigo-50 text-indigo-800',
  signed: 'border-green-300 bg-green-50 text-green-800',
  superseded: 'border-stone-200 bg-white text-stone-500',
};

const fmt = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }) : '–';

export function IrRevisionsPanel({ contractId, refreshKey }: { contractId: string; refreshKey: string }) {
  const q = useTenantQuery(
    'contracts.ir_revisions',
    (cid) => tenantApi.contracts.ir_revisions({ contractId: cid as string }),
    [contractId, refreshKey],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  const revisions = q.data?.revisions ?? [];

  return (
    <div className="mx-auto w-full max-w-3xl p-6">
      <h2 className="mb-1 text-sm font-semibold text-stone-900">Dokumentversioner</h2>
      <p className="mb-4 text-xs text-stone-500">
        En version utfärdas när avtalet skickas för signering eller exporteras. Utfärdade versioner
        ändras aldrig — en ny utfärdan ersätter den förra.
      </p>
      {q.error ? (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-xs text-red-700">
          {(q.error as Error).message}
        </div>
      ) : q.isLoading ? (
        <div className="text-xs text-stone-400">Laddar…</div>
      ) : revisions.length === 0 ? (
        <div className="rounded border border-stone-200 bg-stone-50 p-3 text-xs text-stone-600">
          Ingen version har utfärdats ännu. Dokumentet byggs live medan avtalet redigeras.
        </div>
      ) : (
        <div className="overflow-x-auto rounded border border-stone-200">
          <table className="w-full text-left text-xs">
            <thead className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
              <tr>
                <th className="px-3 py-2">Version</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Dokument</th>
                <th className="px-3 py-2">Utfärdad</th>
                <th className="px-3 py-2">Ersatt</th>
                <th className="px-3 py-2">Kontrollsumma</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {revisions.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2 font-medium text-stone-900">#{r.revision}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${STATUS_CLS[r.status]}`}
                    >
                      {STATUS_LABEL[r.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-stone-600">
                    {r.documentKey} · {r.documentVersion}
                  </td>
                  <td className="px-3 py-2 text-stone-600">{fmt(r.issuedAt ?? r.compiledAt)}</td>
                  <td className="px-3 py-2 text-stone-600">{fmt(r.supersededAt)}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-stone-500" title={r.contentHash}>
                    {r.contentHash.slice(0, 12)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
